import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { pb } from '../lib/pb';

const DataContext = createContext({});

export const DataProvider = ({ children }) => {
  const [data, setData] = useState({
    chantiers: [],
    users: [],
    assignments: [],
    settings: null
  });
  const [loading, setLoading] = useState(true);
  const unsubs = useRef([]);

  const loadAll = async () => {
    try {
      const [chantiers, users, assignments, settingsList] = await Promise.all([
        pb.collection('chantiers').getFullList({ sort: '-created' }),
        pb.collection('users').getFullList({ sort: 'name' }),
        pb.collection('assignments').getFullList({ expand: 'chantier,user' }),
        pb.collection('settings').getFullList()
      ]);

      setData({
        chantiers,
        users,
        assignments,
        settings: settingsList[0] || null
      });
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const refresh = () => loadAll();

  useEffect(() => {
    loadAll();

    const collections = ['chantiers', 'users', 'assignments', 'settings'];
    let isMounted = true;

    const setupSubscriptions = async () => {
      for (const col of collections) {
        try {
          const unsub = await pb.collection(col).subscribe('*', async (e) => {
            if (!isMounted) return;

            // For assignments and chantiers, we need expanded data
            if (col === 'assignments' || col === 'chantiers') {
              try {
                // Targeted refresh for the specific record to get expansions
                if (e.action === 'create' || e.action === 'update') {
                  const expandedRecord = await pb.collection(col).getOne(e.record.id, { expand: 'chantier,user' });
                  
                  setData(prev => {
                    const list = [...(prev[col] || [])];
                    const idx = list.findIndex(x => x.id === e.record.id);
                    if (idx !== -1) {
                      list[idx] = expandedRecord;
                    } else {
                      list.unshift(expandedRecord);
                    }
                    return { ...prev, [col]: list };
                  });
                  return;
                }
              } catch (err) {
                // Fallback to full load if targeted fails
                loadAll();
                return;
              }
            }

            // Normal surgical update for others
            setData(prev => {
              const list = [...(prev[col] || [])];
              if (e.action === 'create') {
                list.unshift(e.record);
              } else if (e.action === 'update') {
                const idx = list.findIndex(x => x.id === e.record.id);
                if (idx !== -1) list[idx] = e.record;
              } else if (e.action === 'delete') {
                return { ...prev, [col]: list.filter(x => x.id !== e.record.id) };
              }
              return { ...prev, [col]: list };
            });
          });
          if (isMounted) unsubs.current.push(unsub);
          else unsub();
        } catch (e) {}
      }
    };

    setupSubscriptions();

    return () => {
      isMounted = false;
      // Final cleanup
      for (const unsub of unsubs.current) {
        if (typeof unsub === 'function') {
          try { unsub(); } catch (e) {}
        }
      }
      unsubs.current = [];
    };
  }, []);

  return (
    <DataContext.Provider value={{ ...data, loading, refresh }}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => useContext(DataContext);
